import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentEditingUser, hasChanges } from "@insite/client-framework/Store/Pages/UserSetup/UserSetupSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import loadUnassignedBillTos from "@NA/Handlers/ADIUserAdminAssignBillTosDataHandler";
import { HiddenProps } from "@insite/mobius/Hidden";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import React, { useContext, useEffect, useState } from "react";
import { CustomAccountBillToModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderPresentationProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import { UserSetupPageContext } from "@insite/content-library/Pages/UserSetupPage";
import submitEditedData from "@NA/Handlers/ADIUserAdminSaveEditedDataHandler";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";

const mapStateToProps = (state: ApplicationState) => ({
    editingUser: getCurrentEditingUser(state),
    disableSaveButton:
        !hasChanges(state) ||
        !!state.pages.userSetup.emailErrorMessage ||
        !!state.pages.userSetup.firstNameErrorMessage ||
        !!state.pages.userSetup.lastNameErrorMessage,
});

const mapDispatchToProps = {
    loadUnassignedBillTos,
    submitEditedData,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ADIUserAdministrationAssignBillTosStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    emptyGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    actionButtonsHidden?: HiddenProps;
    editAssignedCusButton?: ButtonPresentationProps;
    customRedButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    overflowMenu?: OverflowMenuPresentationProps;
    overflowMenuClickables?: ClickablePresentationProps;
    overflowMenuSaveClickableHidden?: HiddenProps;
    tableContainer?: InjectableCss;
    select?: SelectPresentationProps;
    paginationGridContainer: GridContainerProps;
    paginationGridItem?: GridItemProps;
    perPageGridItem?: GridItemProps;
    actionButtonsContainer?: InjectableCss;
    headerActionItemContainer?: GridContainerProps;
    saveButtonStyle?: ButtonPresentationProps;
    leftSideGridItem?: GridItemProps;
    rightSideGridItem?: GridItemProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    spinnerGridItemProps?: GridItemProps;
    pertotalItemCount?: GridItemProps;
    pertotalItemValue?: TypographyProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTablePresentationProps;
    dataTableHead?: DataTableHeadProps;
    defaultHeader?: DataTableHeaderPresentationProps;
    unassignHeader?: DataTableHeaderPresentationProps;
    emailHeader?: DataTableHeaderPresentationProps;
    billToHeader?: DataTableHeaderPresentationProps;
    addressHeader?: DataTableHeaderPresentationProps;
    stateHeader?: DataTableHeaderPresentationProps;
    cityHeader?: DataTableHeaderPresentationProps;
    lastSignInHeader?: DataTableHeaderPresentationProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    usernameCells?: DataTableCellProps;
    nameCells?: DataTableCellProps;
    emailCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    roleCells?: DataTableCellProps;
    lastSignInCells?: DataTableCellProps;
    pagination?: PaginationPresentationProps;
    inputText?: TextFieldPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
}

export const adiUserAdministrationAssignBillTosHeaderStyles: ADIUserAdministrationAssignBillTosStyles = {
    inputText: {
        css: css`
            margin-top: -6px !important;
            width: 280px !important;
            margin-left: 20px !important;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100% !important;
                            margin-left: 0px !important;
                        `,
                        css`
                            width: 100% !important;
                            margin-left: 0px !important;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    container: {
        css: css`
            padding-bottom: 20px;
        `,
    },
    tableContainer: {
        css: css`
            overflow-x: auto;
            width: 100%;
        `,
    },
    titleGridItem: {
        width: [10, 10, 8, 7, 7],
    },
    actionsGridItem: {
        css: css`
            width: 100%;
            /* justify-content: flex-end; */
        `,
    },
    editAssignedCusButton: {
        variant: "secondary",
        css: css`
            width: 365px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    select: {
        labelProps: {
            css: css`
                width: unset;
            `,
        },
        cssOverrides: {
            formField: css`
                width: 218px;
                align-items: center;
                label {
                    padding-top: 2px !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-right: -30px;
                            margin-top: -40px;
                        `,
                        css`
                            margin-right: -30px;
                            margin-top: -40px;
                        `,
                        css`
                            margin-right: -35px;
                            margin-top: 10px;
                        `,
                        css`
                            margin-right: -35px;
                            margin-top: 10px;
                        `,
                        css`
                            margin-right: -35px;
                            margin-top: 10px;
                        `,
                    ])}
            `,
        },
    },
    pertotalItemCount:{
        width: [12, 12, 4, 4, 4],
        as: "h2",
        css:css`
        align-items: center;
        `,       
        
    },

    pertotalItemValue:{
        css:css`
        color: #002856;
        font-size: 16px;
        font-weight: 400;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.36;
        letter-spacing: normal;
        font-family: 'Nunito Sans', sans-serif;
        margin-left: 18px;
        margin-top: 20px;
        `,
    },
    paginationGridContainer: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    paginationGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            justify-content: flex-end;
            align-items: center;
            padding-bottom: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-bottom: 20px;
                            justify-content: center;
                        `,
                        css`
                            padding-bottom: 20px;
                            justify-content: center;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    perPageGridItem: {
        width: [12, 12, 4, 4, 4],

        css: css`
            align-items: center;
            justify-content: flex-end !important;
            padding-right: 0px;
        `,
    },
    actionButtonsContainer: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: flex-end;
            gap: 10px;
        `,
    },
    saveButtonStyle: {
        css: css`
            margin-left: 10px;
        `,
    },
    customRedButton: {
        css: css`
            background-color: #c71e1e !important;
            color: #fff !important;
            border-color: none;
            border: none;
            text-align: center;
            font-size: 13px;
            font-weight: bold;
        `,
    },
    leftSideGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: flex;
                            flex-direction: column;
                            gap: 10px;
                        `,
                        css`
                            display: flex;
                            flex-direction: column;
                            gap: 10px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    rightSideGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: 10px;
        `,
    },
    headerActionItemContainer: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 20px;
            > ${GridOffset} {
                width: 100% !important;
            }
        `,
    },
    spinner: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    spinnerGridItemProps: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    loadingOverlay: {
        css: css`
            div[class^="SpinnerWrapper"] {
                position: absolute !important;
                top: 5% !important;
            }
        `,
    },
};

const styles = adiUserAdministrationAssignBillTosHeaderStyles;

const ADIUserAdministrationAssignBillTos = ({ loadUnassignedBillTos, submitEditedData, history }: Props) => {
    const [userId, setUserId] = useState("");
    const [paginationInfo, setPaginationInfo] = useState<PaginationModel | null>();
    const [userBillToCollection, setUserBillToCollection] = useState<CustomAccountBillToModel[] | null>([]);
    const [searchFilterValue, setSearchFitlerValue] = useState("");
    const [clearSearchFilter, setclearSearchFilter] = useState(false);
    const [isLoading, setisLoading] = useState(false);
    const [dataSubmittingInProgress, setDataSubmittingInProgress] = useState(false);
    const [paginationOptions, setPaginationOptions] = useState({
        page: undefined as number | undefined,
        pageSize: 12 as number | undefined,
    });
    const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);
    const toasterContext = useContext(ToasterContext);
    

    useEffect(() => {
        if (userId) {
            getUsersBillToCollection();
        }
    }, [paginationOptions]);

    useEffect(() => {
        if (searchFilterValue === "" && clearSearchFilter) {
            getUsersBillToCollection("ERPNumber", true);
            setclearSearchFilter(false);
        }
    }, [clearSearchFilter]);

    useEffect(() => {
        const getUserId = localStorage.getItem('userId');
        if (getUserId) {
            setUserId(getUserId);
        }  }, []);

    if (!userId) {
        return null;
    }

    function getUsersBillToCollection(sortByValue: string = "ERPNumber", clearSearchFilter=false) {
        setDataSubmittingInProgress(true);
        loadUnassignedBillTos({
            accountId: userId,
            queryParams: {
                Filter: clearSearchFilter ? "" : searchFilterValue,
                Sort: sortByValue,
                PageSize: paginationOptions.pageSize,
                Page: paginationOptions.page,
                Properties: JSON.stringify({
                    UserInEditId: userId,
                }),
            },
            onComplete(result) {
                if (result.apiResult) {
                    if (result.apiResult.pagination?.totalItemCount === 0) {
                        toasterContext.addToast({
                            body: translate(`No results found`),
                            messageType: "danger",
                            timeoutLength: 5000,
                        });
                    }
                    setUserBillToCollection(result.apiResult.userBillToCollection);
                    setPaginationInfo(result.apiResult.pagination);
                    setisLoading(false);
                    setDataSubmittingInProgress(false);
                }
            },
        });
    }

    const onAssignChangeHandler = (id: string) => {
        let updatedCollection: CustomAccountBillToModel[] | null = JSON.parse(JSON.stringify(userBillToCollection));
        if (updatedCollection) {
            for (let i = 0; i < updatedCollection.length; i++) {
                if (updatedCollection[i].cusromerId === id) {
                    updatedCollection[i].assign = !updatedCollection[i].assign;
                    break;
                }
            }
            setUserBillToCollection(updatedCollection);
        }
    };

    const handleChangePage = (page: number) => {
        setPaginationOptions(prevFields => ({ ...prevFields, page: page }));
    };

    const handleChangeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = Number(event.target.value);
        setPaginationOptions(prevFields => ({ ...prevFields, page: 1, pageSize: newPageSize }));
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        setPaginationOptions(prevFields => ({ ...prevFields, pageSize: newPageSize }));
    };

    const handleMoveToUserSetupPage = () => {
        history.replace(`/MyAccount/UserAdministration/UserSetup?userId=${userId}`);
    };

    const handleSubmitData = () => {
        if (userBillToCollection) {
            setDataSubmittingInProgress(true);
            submitEditedData({
                accountId: userId,
                collection: {
                    UserBillToCollection: userBillToCollection,
                },
                onComplete(result) {
                    if (typeof result.apiResult !== "string") {
                        toasterContext.addToast({
                            body: translate(`Customer Assigned.`),
                            messageType: "success",
                            timeoutLength: 5000,
                        });
                        getUsersBillToCollection();
                    } else {
                        toasterContext.addToast({
                            body: translate(`${result.apiResult}`),
                            messageType: "danger",
                            timeoutLength: 5000,
                        });
                    }
                },
            });
        }
    };

    const headerClick = (sortByValue: string) => {
        getUsersBillToCollection(sortByValue, false);
    };

    const onSelectAllHandler: CheckboxProps["onChange"] = (_, value) => {
        setSelectAllChecked(value);
        let updatedCollection: CustomAccountBillToModel[] | null = JSON.parse(JSON.stringify(userBillToCollection));
        if (updatedCollection) {
            for (let i = 0; i < updatedCollection.length; i++) {
                if (value) {
                    updatedCollection[i].assign = true;
                } else {
                    updatedCollection[i].assign = false;
                }
            }
            setUserBillToCollection(updatedCollection);
        }
    };

    const handleSearchValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchFitlerValue(e.target.value);
    };

    const handleFetchData = () => {
        setDataSubmittingInProgress(true);
        getUsersBillToCollection();
    };

    const handleClearSearch = () => {
        setSearchFitlerValue("");
        setclearSearchFilter(true);
    };

    const redirectToUserSetupPage = () => {
        history.replace(`/MyAccount/UserAdministration/UserSetupBillTo?userId=${userId}`);
    };

    return (
        <>
            <LoadingOverlay {...styles.loadingOverlay} loading={dataSubmittingInProgress}>
                <GridContainer {...styles.container}>
                    <GridContainer {...styles.headerActionItemContainer}>
                        <GridItem {...styles.leftSideGridItem}>
                            <Button
                                {...styles.editAssignedCusButton}
                                type="button"
                                onClick={redirectToUserSetupPage}
                                data-test-selector="tst_userSetup_assignShipToAddress"
                            >
                                {translate("Edit Assigned Customer(s)")}
                            </Button>
                            <TextField
                                {...styles.inputText}
                                value={searchFilterValue}
                                onChange={handleSearchValueChange}
                                max={30}
                                placeholder={translate("Enter Customer Number")}
                            />
                        </GridItem>
                        <GridItem {...styles.rightSideGridItem}>
                            <Button
                                {...styles.customRedButton}
                                type="button"
                                data-test-selector="tst_userSetup_assignShipToAddress"
                                onClick={handleFetchData}
                            >
                                {translate("Search")}
                            </Button>
                            <Button
                                type="button"
                                variant="tertiary"
                                disabled={!searchFilterValue}
                                data-test-selector="tst_userSetup_assignShipToAddress"
                                onClick={handleClearSearch}
                            >
                                {translate("Clear Search")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                    {userBillToCollection && userBillToCollection.length !== 0 ? (
                        <>
                            {paginationInfo && (
                                <GridContainer>
                                    <GridItem {...styles.pertotalItemCount}>
                                        <Typography {...styles.pertotalItemValue}>
                                            <strong>{paginationInfo.totalItemCount}</strong> {translate("Records")}
                                        </Typography>
                                    </GridItem>
                                    <GridItem {...styles.paginationGridItem}>
                                        <Pagination
                                            {...styles.pagination}
                                            resultsCount={paginationInfo.totalItemCount}
                                            currentPage={paginationInfo.page}
                                            resultsPerPage={paginationInfo.pageSize}
                                            resultsPerPageOptions={paginationInfo.pageSizeOptions}
                                            onChangePage={handleChangePage}
                                            onChangeResultsPerPage={handleChangeResultsPerPage}
                                        />
                                    </GridItem>
                                    {paginationInfo.totalItemCount > 12 && (
                                        <GridItem {...styles.perPageGridItem}>
                                            <Select
                                                {...styles.select}
                                                label={translate("Per Page")}
                                                value={paginationInfo.pageSize}
                                                labelPosition="left"
                                                onChange={changeResultsPerPage}
                                                data-test-selector="productListSortSelect"
                                            >
                                                {paginationInfo.pageSizeOptions.filter((option) => option <= 36).map(options => (
                                                    <option key={options} value={options}>
                                                        {options}
                                                    </option>
                                                ))}
                                            </Select>
                                        </GridItem>
                                    )}
                                </GridContainer>
                            )}
                            <GridItem width={12}>
                                <StyledWrapper {...styles.actionButtonsContainer}>
                                    <Button onClick={handleMoveToUserSetupPage} variant="tertiary">
                                        {translate("Cancel")}
                                    </Button>
                                    <Button onClick={handleSubmitData} {...styles.customRedButton}>
                                        {translate("Save")}
                                    </Button>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem width={12}>
                                <StyledWrapper {...styles.tableContainer} data-test-selector="userListUsersTable">
                                    <DataTable {...styles.dataTable}>
                                        <DataTableHead {...styles.dataTableHead}>
                                            <DataTableHeader
                                                {...styles.defaultHeader}
                                                data-test-selector="userListUsersTable_defaultHeader"
                                            >
                                                <Checkbox
                                                    labelPosition="right"
                                                    onChange={onSelectAllHandler}
                                                    checked={selectAllChecked}
                                                >
                                                    {translate("Select")}
                                                </Checkbox>
                                            </DataTableHeader>
                                            <DataTableHeader
                                                {...styles.unassignHeader}
                                                data-test-selector="userListUsersTable_unassignHeader"
                                                onSortClick={() => headerClick("customerNumber")}
                                            >
                                                {translate("Customer #")}
                                            </DataTableHeader>
                                            <DataTableHeader
                                                {...styles.billToHeader}
                                                data-test-selector="tst_userListUsersTable_billToHeader"
                                                onSortClick={() => headerClick("companyName")}
                                            >
                                                {translate("Company Name")}
                                            </DataTableHeader>
                                            <DataTableHeader
                                                {...styles.addressHeader}
                                                data-test-selector="userListUsersTable_addressHeader"
                                            >
                                                {translate("Street Address")}
                                            </DataTableHeader>
                                            <DataTableHeader
                                                {...styles.cityHeader}
                                                data-test-selector="tst_userListUsersTable_cityHeader"
                                                onSortClick={() => headerClick("city")}
                                            >
                                                {translate("City")}
                                            </DataTableHeader>
                                            <DataTableHeader
                                                {...styles.stateHeader}
                                                data-test-selector="tst_userListUsersTable_stateHeader"
                                                onSortClick={() => headerClick("state")}
                                            >
                                                {translate("State")}
                                            </DataTableHeader>
                                        </DataTableHead>
                                        <DataTableBody {...styles.dataTableBody}>
                                            {userBillToCollection &&
                                                userBillToCollection.map(userInfo => (
                                                    <DataTableRow
                                                        {...styles.dataTableRow}
                                                        key={userInfo.cusromerId}
                                                        data-test-selector="userListUsersTable_row"
                                                        data-test-key={userInfo.cusromerId}
                                                    >
                                                        <DataTableCell
                                                            {...styles.nameCells}
                                                            data-test-selector="userListUsersTable_cell_name"
                                                            data-test-key={userInfo.cusromerId}
                                                        >
                                                            <Checkbox
                                                                labelPosition="right"
                                                                onChange={() =>
                                                                    onAssignChangeHandler(userInfo.cusromerId)
                                                                }
                                                                checked={userInfo.assign}
                                                            >
                                                                {translate("Assign")}
                                                            </Checkbox>
                                                        </DataTableCell>

                                                        <DataTableCell
                                                            {...styles.nameCells}
                                                            data-test-selector="userListUsersTable_cell_name"
                                                            data-test-key={userInfo.cusromerId}
                                                        >
                                                            {userInfo.billToNumber}
                                                        </DataTableCell>
                                                        <DataTableCell
                                                            {...styles.usernameCells}
                                                            data-test-selector="userListUsersTable_cell_username"
                                                            data-test-key={userInfo.cusromerId}
                                                        >
                                                            {userInfo.company}
                                                        </DataTableCell>
                                                        <DataTableCell
                                                            {...styles.statusCells}
                                                            data-test-selector="userListUsersTable_cell_status"
                                                            data-test-key={userInfo.cusromerId}
                                                        >
                                                            {userInfo.address}
                                                        </DataTableCell>
                                                        <DataTableCell
                                                            {...styles.roleCells}
                                                            data-test-selector="userListUsersTable_cell_role"
                                                            data-test-key={userInfo.cusromerId}
                                                        >
                                                            {userInfo.city}
                                                        </DataTableCell>
                                                        <DataTableCell
                                                            {...styles.lastSignInCells}
                                                            data-test-selector="userListUsersTable_cell_lastSignIn"
                                                            data-test-key={userInfo.cusromerId}
                                                        >
                                                            {userInfo.state}
                                                        </DataTableCell>
                                                    </DataTableRow>
                                                ))}
                                        </DataTableBody>
                                    </DataTable>
                                </StyledWrapper>
                            </GridItem>
                        </>
                    ) : null}
                </GridContainer>
                {userBillToCollection && userBillToCollection.length !== 0 ? (
                    <StyledWrapper {...styles.actionButtonsContainer}>
                        <Button variant="tertiary">{translate("Cancel")}</Button>
                        <Button onClick={handleSubmitData} {...styles.customRedButton}>
                            {translate("Save")}
                        </Button>
                    </StyledWrapper>
                ) : null}
            </LoadingOverlay>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ADIUserAdministrationAssignBillTos)),
    definition: {
        group: "Basic",
        displayName: "User Admin Assign Bill Tos",
    },
};

export default widgetModule;
